import { ReactElement, useEffect, useMemo, useRef } from 'react'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import { memberCheckTypeToReadableMap } from '@domainConstants'
import { makeCrossSectionLabel } from '@editorUtils'
import { AxiosError } from 'axios'
import { cloneDeep, find } from 'lodash-es'
import { LinearProgress, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { DataGrid, gridClasses } from '@mui/x-data-grid'
import type { GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { DefaultValues } from '@editor/pages/StructuralPlanning/components/MultiEdit/components/MultiEditCrossSection'
import { useMutation } from '@tanstack/react-query'
import { UtilizationC90TextIcon, UtilizationTextIcon } from '@ui/icons/misc'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useElementLabel } from '@editorHooks'
import { GetCalcChecksForElement, getCalcChecksForElementRequest2 } from '@queries'
import { usageClassToLabel } from 'src/constants/elements'
import { resolveAssemblyStandardCrossSectionFromModel } from 'src/state/utils/deriveElementAssignments'
import { getElementUtilizationFromStructuralChecks } from './utils'

type ElementCSRow = {
  elementGuid: string
  label: string
  qs: string
  policy?: string
  nkl: string
  eta?: number
  etaC?: number
  etaCheckType?: MemberCheckType
  etaCCheckType?: MemberCheckType
  etaIsVibrationError?: boolean
  etaCIsVibrationError?: boolean
}

const policyLabel = (policy: CrossSectionPolicy | undefined): string | undefined => {
  if (policy === 'AssemblyStandard') return 'aus Aufbau'
  if (policy === 'UserDefined') return 'manuell'
  return undefined
}

const formatUtilization = (value: number | undefined): string =>
  value !== undefined && Number.isFinite(value) ? value.toFixed(2) : ''

const utilizationCellClassName = (
  value: number | undefined,
  isVibrationError: boolean | undefined,
): string => {
  if (isVibrationError) return 'overutilized'
  if (value === undefined || !Number.isFinite(value)) return ''
  if (value >= SLIGHTLY_OVERUTILIZED_THRESHOLD) return 'overutilized'
  if (value >= 1.0) return 'slightly-overutilized'
  return ''
}

const utilizationGridSx = (theme: Theme) => ({
  [`& .${gridClasses.cell}.slightly-overutilized`]: {
    backgroundColor: theme.results.utilization.slightlyOverutilized.light,
  },
  [`& .${gridClasses.cell}.overutilized`]: {
    backgroundColor: theme.results.utilization.overutilized.light,
  },
})

const renderUtilizationCell = (
  value: number | undefined,
  isVibrationError: boolean | undefined,
  checkType: MemberCheckType | undefined,
  dataCy: string,
): ReactElement => {
  const available = value !== undefined && Number.isFinite(value)
  const display = isVibrationError ? 'ERR' : available ? formatUtilization(value) : 'n/a'
  const title = checkType ? memberCheckTypeToReadableMap[checkType] ?? checkType : 'n/a'

  const cellContent = (
    <Typography
      sx={{ fontWeight: 'inherit', lineHeight: 1, display: 'flex', alignItems: 'center' }}
      data-cy={dataCy}
    >
      {display}
    </Typography>
  )

  return title ? <Tooltip title={title}>{cellContent}</Tooltip> : cellContent
}

const etaColumn: GridColDef<ElementCSRow> = {
  field: 'eta',
  headerName: 'η',
  width: 56,
  minWidth: 56,
  align: 'center',
  headerAlign: 'center',
  renderHeader: () => <UtilizationTextIcon />,
  renderCell: (params: GridRenderCellParams<ElementCSRow>) =>
    renderUtilizationCell(
      params.row.eta,
      params.row.etaIsVibrationError,
      params.row.etaCheckType,
      `element-cs-table-eta-${params.row.elementGuid}`,
    ),
  cellClassName: (params: GridCellParams<ElementCSRow>) =>
    utilizationCellClassName(params.row.eta, params.row.etaIsVibrationError),
  display: 'flex',
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
}

const etaCColumn: GridColDef<ElementCSRow> = {
  field: 'etaC',
  headerName: 'ηc,90',
  width: 64,
  minWidth: 64,
  align: 'center',
  headerAlign: 'center',
  renderHeader: () => <UtilizationC90TextIcon />,
  renderCell: (params: GridRenderCellParams<ElementCSRow>) =>
    renderUtilizationCell(
      params.row.etaC,
      params.row.etaCIsVibrationError,
      params.row.etaCCheckType,
      `element-cs-table-eta-c-${params.row.elementGuid}`,
    ),
  cellClassName: (params: GridCellParams<ElementCSRow>) =>
    utilizationCellClassName(params.row.etaC, params.row.etaCIsVibrationError),
  display: 'flex',
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
}

/**
 * Use this component to display cross section information of the passed
 * elementGuids
 */
const ElementCSTable = (): ReactElement => {
  const { projectId } = useParams()
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()
  const elements = useWatch<DefaultValues>({ name: 'elements' }) as DefaultValues['elements']
  const crossSection = useWatch<DefaultValues>({ name: 'crossSection' })
  const formPolicy = useWatch<DefaultValues>({ name: 'crossSectionPolicy' }) as
    | CrossSectionPolicy
    | undefined
  const model = useModelStore(state => state.model)
  const showEtaColumns = useMemo(() => {
    return elements.some(element => element.checks.length > 0)
  }, [elements])

  const getLabel = useElementLabel()

  const showPolicyColumn = useMemo(
    () => elements.some(element => element.crossSectionPolicy != null),
    [elements],
  )

  const columns = useMemo<GridColDef<ElementCSRow>[]>(() => {
    const base: GridColDef<ElementCSRow>[] = [
      { field: 'label', headerName: 'Element', flex: 0, minWidth: 80 },
      { field: 'qs', headerName: 'Querschnitt', flex: 1, minWidth: 100 },
    ]
    base.push({ field: 'nkl', headerName: 'Nutzungsklasse', flex: 1, minWidth: 80 })
    if (showPolicyColumn) {
      base.push({
        field: 'policy',
        headerName: 'Quelle',
        flex: 0,
        minWidth: 45,
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      })
    }

    if (showEtaColumns) {
      base.push(etaColumn, etaCColumn)
    }

    return base
  }, [showEtaColumns, showPolicyColumn])

  const rows: ElementCSRow[] = useMemo(() => {
    return elements?.map(element => {
      const utilization = getElementUtilizationFromStructuralChecks(element.guid, element.checks)
      const eta = utilization.eta?.max_utilization
      const etaC = utilization.etaC?.max_utilization
      // Before "Ändern", show each element's configured source so Quelle matches
      // the Querschnitt column. After unlock, follow the active form tab.
      const policy = policyLabel(
        crossSection ? formPolicy ?? element.crossSectionPolicy : element.crossSectionPolicy,
      )

      if (!element.crossSection) {
        return {
          elementGuid: element.guid,
          label: getLabel(element.guid),
          qs: '[nicht gefunden]',
          policy,
          nkl: '[nicht gefunden]',
          eta,
          etaC,
          etaCheckType: utilization.eta?.check_type,
          etaCCheckType: utilization.etaC?.check_type,
          etaIsVibrationError: utilization.eta?.check_type === 'Vibration',
          etaCIsVibrationError: utilization.etaC?.check_type === 'Vibration',
        }
      }

      // Before "Ändern" (`crossSection` unset) keep each element's real CS.
      // After unlock: AssemblyStandard uses per-element CS (from calc-checks);
      // UserDefined prefers the shared form CS until the response lands.
      const usedCrossSection = !crossSection
        ? element.crossSection
        : formPolicy === 'AssemblyStandard'
        ? element.crossSection
        : crossSection

      const crossSectionLabel = makeCrossSectionLabel(usedCrossSection)

      return {
        elementGuid: element.guid,
        label: getLabel(element.guid),
        qs: crossSectionLabel || 'Fehler',
        policy,
        nkl:
          usageClassToLabel[
            (usedCrossSection as { usage_class: keyof typeof usageClassToLabel }).usage_class
          ] ?? 'n/a',
        eta,
        etaC,
        etaCheckType: utilization.eta?.check_type,
        etaCCheckType: utilization.etaC?.check_type,
        etaIsVibrationError: utilization.eta?.check_type === 'Vibration',
        etaCIsVibrationError: utilization.etaC?.check_type === 'Vibration',
      }
    })
  }, [crossSection, elements, formPolicy, getLabel])

  const { getValues, setValue } = useFormContext<DefaultValues>()

  const abortController = useRef(new AbortController())
  const elementsSignature = useMemo(() => elements.map(el => el.guid).join('|'), [elements])
  const prevElementsSignatureRef = useRef<string | null>(null)

  const { mutate: updateChecks, isLoading: isUpdatingChecks } = useMutation({
    mutationKey: [projectId, 'updateChecksMultiEdit'],
    mutationFn: async ({ signal }: { signal: AbortSignal }) => {
      const crossSection = getValues('crossSection')
      const crossSectionPolicy = getValues('crossSectionPolicy')

      // Editing still gated behind "Ändern" — keep original checks/CS.
      if (!crossSection) return []

      const elements = getValues('elements')

      const isAssemblyStandardPolicy = crossSectionPolicy === 'AssemblyStandard'
      const data: { elementGuid: string; response: CrossSectionSuggestion }[] = []
      for (const element of elements) {
        // Outside Results there are no member-check settings — never call calc-checks.
        if (!element.checkSettings || element.checks.length === 0) continue

        const supportingElementCSConfig = element.supportingElementCSConfig.map(config => {
          const isInElements = !!find(elements, item => item.guid === config.element_guid)

          const supportingElementCrossSection = isAssemblyStandardPolicy
            ? (resolveAssemblyStandardCrossSectionFromModel(model, config.element_guid) as
                | CrossSection
                | undefined)
            : undefined

          return {
            element_guid: config.element_guid,
            cross_section: isInElements
              ? supportingElementCrossSection ?? crossSection
              : supportingElementCrossSection ?? config.cross_section,
          }
        })

        const crossSectionForElement = isAssemblyStandardPolicy
          ? resolveAssemblyStandardCrossSectionFromModel(model, element.guid) ?? crossSection
          : crossSection
        const requestData: GetCalcChecksForElement = {
          element_guid: element.guid,
          crossSection: crossSectionForElement as CrossSectionTimberOrCLTOrSteel,
          settings: element.checkSettings,
          supporting_elements_cs_config: supportingElementCSConfig,
        }
        const response = await getCalcChecksForElementRequest2.request(projectId as string, {
          requestData,
          options: { signal },
        })
        data.push({
          response: cloneDeep(response),
          elementGuid: element.guid,
        })
      }
      const elementData: DefaultValues['elements'] = elements.map(element => {
        const elementResponse = find(data, item => item.elementGuid === element.guid)
        if (!elementResponse) return element

        return {
          ...element,
          // Response CS is authoritative (esp. AssemblyStandard / aus Aufbau).
          crossSection: elementResponse.response.cross_section ?? element.crossSection,
          checks: elementResponse.response.checks as CombinedPositionCheck[],
        }
      })

      return elementData
    },

    onSuccess: (data: DefaultValues['elements']) => {
      if (data.length === 0) return

      setValue('elements', data)
    },
    onError: (error: AxiosError) => {
      if (error.code === 'ERR_CANCELED' || error.message === 'canceled') return
      enqueueSnackbar(
        buildErrorMessage(error, 'Fehler beim Aktualisieren der Nachweiseinstellungen'),
        { variant: 'error' },
      )
    },
  })

  const { dirtyFields } = useFormState<DefaultValues>()

  useEffect(() => {
    // No utilizations outside Results — skip calc-checks entirely (avoids 422s when
    // member-check results/settings are missing in Structural Planning multi-edit).
    if (!showEtaColumns) {
      prevElementsSignatureRef.current = elementsSignature
      return
    }

    // No shared form CS means "Ändern" was not pressed — do not recompute.
    if (!crossSection) {
      prevElementsSignatureRef.current = elementsSignature
      return
    }

    const selectionChanged = prevElementsSignatureRef.current !== elementsSignature
    const shouldUpdate =
      dirtyFields.crossSection ||
      // Policy toggle changes the CS resolution strategy (UserDefined vs AssemblyStandard).
      dirtyFields.crossSectionPolicy ||
      selectionChanged

    if (shouldUpdate) {
      abortController.current.abort()
      abortController.current = new AbortController()
      updateChecks({ signal: abortController.current.signal })
      prevElementsSignatureRef.current = elementsSignature
    }
  }, [
    crossSection,
    dirtyFields.crossSection,
    dirtyFields.crossSectionPolicy,
    elementsSignature,
    formPolicy,
    showEtaColumns,
    updateChecks,
  ])

  return (
    <Stack direction="column">
      {isUpdatingChecks && <LinearProgress />}
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={row => row.elementGuid}
        hideFooterPagination
        hideFooterSelectedRowCount
        hideFooter
        autosizeOnMount
        autosizeOptions={{ includeHeaders: false, expand: true }}
        disableColumnSorting
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        density="compact"
        sx={utilizationGridSx(theme)}
      />
    </Stack>
  )
}

export default ElementCSTable
